import { ROTransaction, RWTransaction, runWithROTransaction } from '../../../db'
import {
  createObj,
  enumGetValues,
  Context,
  validateRequiredId,
  PaginatedResult,
  BaseDBServiceOpts,
  CoreDict,
  BaseDBService,
  getPage,
  Id,
  notNull,
  throwObjectNotFound,
  throwAccessDenied,
  validateOptionalNumber,
  throwInvalidParameter,
  throwInvalidOperation,
} from '../../../common'
import { CreateEventMode, ModelEvent, ModelEventsProcessor } from '../../events'
import { MunicipalEntitiesRepository } from '../../municipalEntity'
import { Permissions } from '../../authorization'
import { UsersRepository } from '../../users'
import {
  ActualIssueProduct,
  ActualIssueProductOptions,
  ActualIssueProductsRepository,
  ActualIssueProductsService,
  AddActualIssueProductOpts,
  AddActualIssueProductValues,
  GroupedByMEActualIssueProduct,
} from './actualIssueProducts'
import { cloneDeep, forIn, groupBy, set, toArray } from 'lodash'
import { CitizenCategory } from '../../citizens'
import { PRODUCT_CATEGORIES_ACTUAL_COMSUMPTION_COEFFICIENTS, ProductCategory } from '../../productCategories'
import { ConsumptionStandardsService } from '../../consumptionStandards'

enum EventType {
  ACTUAL_ISSUE_PRODUCT_ADD = 'ACTUAL_ISSUE_PRODUCT_ADD',
}

type AddActualIssueProductPayload = {
  dataEntryDate: string
  municipalEntityId: string
  values: string
}

interface DBActualIssueProductsServiceOpts extends BaseDBServiceOpts<CoreDict> {
  usersRepository: UsersRepository
  municipalEntitiesRepository: MunicipalEntitiesRepository
  actualIssueProductsRepository: ActualIssueProductsRepository
  consumptionStandardsService: ConsumptionStandardsService
}

function convertActualIssueProduct(aip: ActualIssueProduct): GroupedByMEActualIssueProduct {
  return {
    municipalEntityId: aip.municipalEntityId,
    values: aip.values,
  }
}
export class DBActualIssueProductsService
  extends BaseDBService<DBActualIssueProductsServiceOpts, CoreDict>
  implements ActualIssueProductsService
{
  constructor(opts: DBActualIssueProductsServiceOpts) {
    super('DBActualIssueProductsService', opts)
    this.logger.debug({}, '<712b1764> Init')
  }
  private validateUserID = (userID?: string) => validateRequiredId('<aac7eb43> User ID', userID)
  private validateMunicipalEntityID = (municipalEntityId?: string) =>
    validateRequiredId('<192e325f> Municipal entity ID', municipalEntityId)
  private validateValue = (citizenCategory: CitizenCategory, productCategory: ProductCategory, value?: number) =>
    validateOptionalNumber(`<07526749> ${citizenCategory} ${productCategory} Value`, value, { minValue: 0 })

  async formReport(
    ctx: Context,
    opts?: ActualIssueProductOptions | undefined,
  ): Promise<PaginatedResult<GroupedByMEActualIssueProduct>> {
    const actualIssueProducts = await runWithROTransaction(this.opts.dbConnection, async tx => {
      if (!(await this.opts.permissionsService.hasPermission(ctx, Permissions.ACTUAL_ISSUE_PRODUCTS_VIEW))) {
        await this.opts.permissionsService.checkPermission(ctx, Permissions.ME_ACTUAL_ISSUE_PRODUCTS_VIEW)

        if (!opts?.filter?.municipalEntityId) {
          const user = await this.opts.usersRepository.findById(tx, this.validateUserID(ctx.userId))
          opts = { ...opts, filter: { ...opts?.filter, municipalEntityId: user?.municipalEntityId } }
        }
        await this.checkAccessToMunicipalEntity('<8d7caccd>', ctx, tx, [opts?.filter?.municipalEntityId])
      }

      return this.opts.actualIssueProductsRepository.findAll(tx, opts)
    })

    const groupedByME: ActualIssueProduct[][] = toArray(groupBy(actualIssueProducts, 'municipalEntityId'))
    return getPage(this.sumActualIssueProducts(groupedByME).map(convertActualIssueProduct), opts?.paging)
  }

  async add(ctx: Context, opts: AddActualIssueProductOpts): Promise<Id> {
    const event = await runWithROTransaction(this.opts.dbConnection, async tx => {
      if (!(await this.opts.permissionsService.hasPermission(ctx, Permissions.ACTUAL_ISSUE_PRODUCTS_VIEW))) {
        await this.opts.permissionsService.checkPermission(ctx, Permissions.ME_ACTUAL_ISSUE_PRODUCTS_VIEW)

        if (!opts.municipalEntityId) {
          const user =
            (await this.opts.usersRepository.findById(tx, this.validateUserID(ctx.userId))) ??
            throwObjectNotFound(`<607faac3> ${this.translate(ctx, d => d.users.notFoundByIdError, ctx.userId)}`)

          opts.municipalEntityId ||=
            user.municipalEntityId ??
            throwInvalidParameter(`<f03235b1> ${this.translate(ctx, d => d.municipalEntities.notGiven)}`)
        }

        await this.checkAccessToMunicipalEntity('<3b5ac8d1>', ctx, tx, [opts.municipalEntityId])
      }

      await this.checkForMunicipalEntityExists(ctx, tx, this.validateMunicipalEntityID(opts.municipalEntityId))

      const consumptionStandards = await this.opts.consumptionStandardsService.get({ ...ctx, internal: true })
      const values = opts.values

      forIn(CitizenCategory, citizenCategory => {
        forIn(ProductCategory, productCategory => {
          let couponsCount = values[citizenCategory][productCategory]?.couponsCount

          if (couponsCount === undefined) {
            set(values, `${citizenCategory}.${productCategory}.couponsCount`, 0)
            couponsCount = 0
          }

          set(
            values,
            `${citizenCategory}.${productCategory}.actualConsumption`,
            consumptionStandards[citizenCategory][productCategory] *
              couponsCount *
              PRODUCT_CATEGORIES_ACTUAL_COMSUMPTION_COEFFICIENTS[productCategory],
          )
        })
      })
      const municipalEntityId = this.validateMunicipalEntityID(opts.municipalEntityId)
      const entityId = this.opts.idGenerator.generate()
      await this.validateValues(values)
      return await this.opts.eventCreator.create(ctx, tx, {
        mode: CreateEventMode.NEW,
        entityId,
        type: EventType.ACTUAL_ISSUE_PRODUCT_ADD,
        municipalEntityId,
        payload: createObj<AddActualIssueProductPayload>({
          dataEntryDate: opts.dataEntryDate.toISOString(),
          municipalEntityId,
          values: JSON.stringify(opts.values),
        }),
      })
    })

    await this.opts.eventDispatcher.processEvents(ctx, [event])

    return event.entityId
  }

  private sumActualIssueProducts(group: ActualIssueProduct[][]): ActualIssueProduct[] {
    return group
      .map(groupItem =>
        groupItem.reduce<ActualIssueProduct | undefined>((previousValue, currentValue) => {
          if (!previousValue) return cloneDeep(currentValue)
          const newValue = cloneDeep(previousValue)

          forIn(CitizenCategory, citizenCategory => {
            forIn(ProductCategory, productCategory => {
              const curValueCategory = currentValue.values[citizenCategory][productCategory]
              const newValueCategory = newValue.values[citizenCategory][productCategory]
              newValueCategory.couponsCount += curValueCategory.couponsCount
              newValueCategory.actualConsumption += curValueCategory.actualConsumption
            })
          })
          return newValue
        }, undefined),
      )
      .filter(notNull)
  }

  private async checkAccessToMunicipalEntity(
    errorCode: string,
    ctx: Context,
    tx: ROTransaction,
    meIds: (string | undefined)[],
  ): Promise<void> {
    const filteredMEIds = meIds?.filter(id => id != undefined)
    if (!filteredMEIds.length) {
      throwObjectNotFound(`${errorCode} ${this.translate(ctx, d => d.municipalEntities.notGiven)}`)
    }

    const userID = this.validateUserID(ctx.userId)
    const user =
      (await this.opts.usersRepository.findById(tx, userID)) ??
      throwObjectNotFound(`<5dec5501> ${this.translate(ctx, d => d.users.notFoundByIdError, userID)}`)

    filteredMEIds.forEach(id => {
      if (user.municipalEntityId != id) {
        throwAccessDenied(
          `${errorCode} ${this.opts.translationService.translate(ctx.langCode, d => d.municipalEntities.accessDenied)}`,
        )
      }
    })
  }

  private async validateValues(values: AddActualIssueProductValues) {
    forIn(CitizenCategory, citizenCategory => {
      forIn(ProductCategory, productCategory => {
        this.validateValue(citizenCategory, productCategory, values[citizenCategory][productCategory]?.couponsCount)
      })
    })
  }

  private async checkForMunicipalEntityExists(ctx: Context, tx: ROTransaction, id: Id) {
    ;(await this.opts.municipalEntitiesRepository.findByIdNonDeleted(tx, id)) ??
      throwObjectNotFound(`<e673a52a> ${this.translate(ctx, d => d.municipalEntities.notFoundByIdError, id)}`)
  }
}

export class ActualIssueProductsModelEventProcessor implements ModelEventsProcessor {
  supportedEventTypes: string[] = enumGetValues(EventType)

  constructor(private opts: { actualIssueProductsRepository: ActualIssueProductsRepository }) {}

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  createMergeEvent(ctx: Context, tx: ROTransaction, entityId: string): Promise<ModelEvent> {
    throwInvalidOperation('<588f3cbd> Merge not supported')
  }

  async process(ctx: Context, tx: RWTransaction, event: ModelEvent): Promise<boolean> {
    switch (event.type as EventType) {
      case EventType.ACTUAL_ISSUE_PRODUCT_ADD: {
        const p = event.payload as AddActualIssueProductPayload
        await this.opts.actualIssueProductsRepository.save(tx, {
          id: event.entityId,
          createdAt: event.createdAt,
          dataEntryDate: new Date(p.dataEntryDate),
          municipalEntityId: p.municipalEntityId,
          values: p.values,
        })
        return true
      }
    }
    return false
  }
}
